import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Refresh } from '@mui/icons-material';
import api from '../services/api';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isTr = language === 'tr';
  const packageType = searchParams.get('package');
  const transactionId = searchParams.get('transaction_id');
  const statusParam = searchParams.get('status');
  const [verifying, setVerifying] = useState(!!transactionId && statusParam !== 'success' && statusParam !== 'already_processed');
  const [verificationResult, setVerificationResult] = useState<string | null>(null);

  useEffect(() => {
    if (transactionId && verifying) {
      const verifyTransaction = async () => {
        try {
          const response = await api.post(`/payment/verify-transaction?transaction_id=${transactionId}`);
          const result = response.data;
          setVerificationResult(result.status);
          setVerifying(false);
          if (result.status === 'success') {
            console.log(`Added ${result.tokens_added} tokens to account`);
          }
        } catch (error) {
          console.error('Error verifying transaction:', error);
          setVerificationResult('error');
          setVerifying(false);
        }
      };
      verifyTransaction();
    }
  }, [transactionId, verifying]);

  useEffect(() => {
    if (!verifying) {
      const timer = setTimeout(() => navigate('/dashboard'), 5000);
      return () => clearTimeout(timer);
    }
  }, [navigate, verifying]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-3xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-success">/✓</span> · {isTr ? 'ÖDEME ALINDI' : 'PAYMENT RECEIVED'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'TEŞEKKÜRLER' : 'THANK YOU'}</span>
        </div>

        <div className="border-t border-hairline pt-16 mb-12">
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Hoşgeldin, <em>tokenlar</em> hesabında.</> : <>Welcome — <em>tokens</em> are in.</>}
          </h1>

          {verifying ? (
            <div className="flex items-center gap-3 mt-8">
              <Refresh className="animate-spin text-accent" sx={{ fontSize: 18 }} />
              <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim">
                {isTr ? 'ÖDEME DOĞRULANIYOR…' : 'VERIFYING PAYMENT…'}
              </p>
            </div>
          ) : (
            <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
              {(() => {
                if (verificationResult === 'success') {
                  return isTr
                    ? 'Ödeme başarıyla tamamlandı. Tokenların hesabına eklendi — yeni bir analiz başlatabilirsin.'
                    : 'Payment completed successfully. Your tokens are in — start a new analysis whenever you like.';
                } else if (verificationResult === 'already_processed') {
                  return isTr
                    ? 'Ödeme zaten kaydedildi. Tokenların hesabında.'
                    : 'Payment already recorded. Your tokens are in your account.';
                } else {
                  return isTr
                    ? 'Aboneliğin oluşturuldu, tokenların eklendi.'
                    : 'Your subscription is set, tokens added.';
                }
              })()}
            </p>
          )}
        </div>

        {packageType && (
          <div className="border border-hairline p-6 mb-12 max-w-md">
            <span className="eyebrow-mute mb-2 block">{isTr ? 'PAKET' : 'PACKAGE'}</span>
            <span className="font-serif italic text-2xl text-ink">{packageType}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start gap-3">
          <button onClick={() => navigate('/dashboard')} className="btn-accent">
            {isTr ? 'PANELE GİT' : 'GO TO DASHBOARD'}
            <span aria-hidden>→</span>
          </button>
          <button onClick={() => navigate('/upload')} className="btn-ghost">
            {isTr ? 'YENİ ANALİZ BAŞLAT' : 'START NEW ANALYSIS'}
            <span aria-hidden>→</span>
          </button>
        </div>

        <p className="mt-12 font-mono text-[10px] tracking-[0.2em] uppercase text-ink-mute">
          {isTr ? '5 SANİYE İÇİNDE PANELE YÖNLENDİRİLİYORSUN…' : 'REDIRECTING TO DASHBOARD IN 5 SECONDS…'}
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
